import { ChangeDetectionStrategy, Component, DestroyRef, effect, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { getAnimationAppearOnTrigger } from '@examdojo/animation';
import { SyllabusQuery } from '@examdojo/category/v2';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { ErrorHandlerService } from '@examdojo/error-handling';
import {
  getReferenceQuestionStatusLabel,
  ReferenceQuestionService,
  ReferenceQuestionStatus,
  ReferenceQuestionStatusList,
  ReferenceQuestionViewStoreModel,
} from '@examdojo/reference-question';
import { LoaderComponent } from '@examdojo/ui/loader';
import {
  ColDef,
  FilterModel,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowClickedEvent,
  SortModelItem,
  ValueFormatterParams,
} from 'ag-grid-community';
import { EMPTY, finalize, first, tap } from 'rxjs';
import { CmsUrlParts } from '../../cms/cms.model';
import { AgGirdColumnStateManager } from '../../shared/ag-grid/ag-gird-column-state.manager';
import { ReferenceQuestionListStatusCellRendererComponent } from '../list/reference-question-list-status-cell-renderer.component';
import { ReferenceQuestionListService } from '../services/reference-question-list.service';

const REF_QUESTION_LIST_VIEW_STATE_STORAGE_KEY = 'scoped-reference-question-grid-state';

@Component({
  selector: 'exam-scoped-reference-question-list',
  templateUrl: './scoped-reference-question-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [MasterListViewComponent, LoaderComponent],
  providers: [provideSimpleListView(ReferenceQuestionListService)],
  animations: [getAnimationAppearOnTrigger()],
})
export class ScopedReferenceQuestionListComponent {
  constructor(
    private readonly router: Router,
    private readonly service: ReferenceQuestionService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly destroyRef: DestroyRef,
    private readonly syllabusQuery: SyllabusQuery,
  ) {
    effect(() => {
      const sourceId = this.sourceId();
      if (sourceId) {
        this.columnStateManager.overrideStorageKey(`${REF_QUESTION_LIST_VIEW_STATE_STORAGE_KEY}-${sourceId}`);
      }
    });
  }
  readonly agGridAdditionalClass = input<string>('ag-theme-basic-table small-height');
  readonly sourceId = input<string>();
  readonly gridApi = signal<GridApi<ReferenceQuestionViewStoreModel> | null>(null);
  readonly isLoading = signal<boolean>(true);

  readonly columnDefs: Array<ColDef<ReferenceQuestionViewStoreModel>> = [
    {
      field: 'question_number',
      headerName: 'Question Number',
      valueGetter: ({ data }) => {
        return data?.question_number ?? '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      valueGetter: ({ data }) => {
        return data?.id ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      minWidth: 150,
      cellClass: 'truncate',
      cellRenderer: ReferenceQuestionListStatusCellRendererComponent,
      filterParams: {
        values: ReferenceQuestionStatusList,
        valueFormatter: (
          params: ValueFormatterParams<ReferenceQuestionViewStoreModel, ReferenceQuestionStatus | null>,
        ) => getReferenceQuestionStatusLabel(params.value),
      },
    },
  ];

  readonly serverSideDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      const { startRow, endRow, sortModel, filterModel } = params.request;
      const sortBy: SortModelItem[] = sortModel.length ? sortModel : [{ colId: 'updated_at', sort: 'desc' }];

      this.service
        .fetchPaginatedListView({
          startRow,
          endRow: endRow ? endRow - 1 : undefined,
          sortBy: sortBy,
          filterBy: {
            ...filterModel,
            source_id: {
              filterType: 'set',
              values: [this.sourceId()],
            },
          } as FilterModel,
        })
        .pipe(
          first(),
          tap({
            next: (data) => {
              params.success({ rowData: data.data, rowCount: data.totalCount });
              this.columnStateManager.initialRowsLoaded.set(true);
            },
            error: () => {
              params.fail();
            },
          }),
          this.errorHandlerService.catchError(
            `[QuestionListComponent.getRows]: failed to load the questions`,
            () => EMPTY,
            { toast: `Could not load reference questions` },
          ),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    },
  };

  readonly gridOptions: GridOptions<ReferenceQuestionViewStoreModel> = {
    pagination: true,
    paginationAutoPageSize: true,
    cacheBlockSize: 100,
    rowModelType: 'serverSide',
    serverSideDatasource: this.serverSideDatasource,
    suppressCellFocus: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No reference questions found',
      body: 'Please try with another filter',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: 'single',
    getContextMenuItems: (event) => [
      {
        name: 'Open in New Tab',
        icon: `<i class="fa-light fa-arrow-up-right-from-square"></i>`,
        action: () => {
          if (!event.node?.data) {
            return;
          }

          const activeSyllabus = this.syllabusQuery.getActive();
          assertNonNullable(activeSyllabus, 'activeSyllabus');

          window.open(
            `/${CmsUrlParts.Syllabus}/${activeSyllabus.slug}/${CmsUrlParts.ReferenceQuestions}/${event.node?.data.id}`,
            '_blank',
            'noopener,noreferrer',
          );
        },
      },
    ],

    rowClass: 'cursor-pointer',
    onPaginationChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onSortChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onFilterChanged: () => {
      this.columnStateManager.saveGridState();
    },

    getRowId: (params) => (params.data?.id ? `${params.data.id}` : ''),
  };

  readonly columnStateManager = new AgGirdColumnStateManager(
    this.gridOptions,
    REF_QUESTION_LIST_VIEW_STATE_STORAGE_KEY,
  );

  openReferenceQuestion(event: RowClickedEvent<ReferenceQuestionViewStoreModel, void>) {
    if (!event.data) {
      return;
    }

    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate([
      '/',
      CmsUrlParts.Syllabus,
      activeSyllabus.slug,
      CmsUrlParts.ReferenceQuestions,
      event.data.id,
    ]);
  }

  onGridReady(event: GridReadyEvent<ReferenceQuestionViewStoreModel>) {
    this.gridApi.set(event.api);

    this.columnStateManager.gridApi.set(event.api);
    this.columnStateManager.gridReady.set(true);
    this.columnStateManager.loadGridState();
  }
}
